import React, { useState, useEffect, useRef } from "react"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Tabs,
  Typography,
  Tab,
  Box,
  CircularProgress,
} from "@mui/material"
import PropTypes from "prop-types"
import i18n from "i18n"
import CloseIcon from "@mui/icons-material/Close"
import GeneralTab from "./GeneralTab"
import ContabilityTab, { SEPA_FIELDS } from "./ContabilityTab"

// contability is rebuilt field by field in three places (initial state, loading an
// existing company, resetting for a new one). Centralised so a field added to the tab
// cannot be silently dropped on load, which is how the SEPA values were being lost.
const emptyContability = () => ({
  responsibleName: "",
  responsibleEmail: "",
  paymentMethod: "transfer",
  code: "",
  paymentPeriod: undefined,
  accountNumber: "",
  mandateRef: "",
  creditorId: "",
})

const contabilityFrom = (source = {}) => ({
  ...emptyContability(),
  responsibleName: source.responsibleName || "",
  responsibleEmail: source.responsibleEmail || "",
  paymentMethod: source.paymentMethod || "transfer",
  code: source.code || "",
  paymentPeriod: source.paymentPeriod || undefined,
  accountNumber: source.accountNumber || "",
  mandateRef: source.mandateRef || "",
  creditorId: source.creditorId || "",
})

const TAB_GENERAL = 0
const TAB_ACCOUNTING = 1
import NotesTab from "./NotesTab"
import { useQueryClient } from "@tanstack/react-query"
import {
  useCompanyTags,
  useCreateCompanyTag,
  useDeleteCompanyTag,
  useDeleteCompany,
  useCompany,
} from "../hooks/useContacts"

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
}

const CompanyModal = ({
  open,
  onClose,
  onSave,
  organizationId,
  company,
  contactFullName,
  onReady,
}) => {
  const companyId = company?.SK || ""
  const {
    data: fetchedCompany,
    isLoading: isCompanyLoading,
    isFetching: isCompanyFetching,
    refetch: refetchCompany,
  } = useCompany(organizationId, companyId, false)
  const isEditMode = Boolean(companyId)
  const [isWaitingFreshCompany, setIsWaitingFreshCompany] = useState(false)
  const previousOpenRef = useRef(false)
  const previousCompanyIdRef = useRef(companyId)
  const isStartingEditOpenCycle =
    open &&
    isEditMode &&
    (!previousOpenRef.current || previousCompanyIdRef.current !== companyId)
  const hasRequestedCompanyData =
    !isEditMode || (Boolean(fetchedCompany) && fetchedCompany?.SK === companyId)
  const shouldOpen =
    open &&
    (!isEditMode ||
      (!isStartingEditOpenCycle &&
        !isWaitingFreshCompany &&
        hasRequestedCompanyData))
  const queryClient = useQueryClient()
  const [fields, setFields] = useState({
    companyName: "",
    type: "company",
    id: "",
    category: [],
    fiscalIdType: "",
    fiscalIdNumber: "",
    cp: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    country: "",
    contability: emptyContability(),
    notes: "",
  })
  const [tabValue, setTabValue] = useState(0)
  const [errors, setErrors] = useState({})
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // What blocks saving, and which tab holds it: the footer banner names the tab so a
  // greyed-out button is never a dead end for someone looking at the wrong one.
  const missingName = !fields.companyName?.trim()
  const invalidSepa = SEPA_FIELDS.some(({ field, isValid }) => {
    const value = fields.contability?.[field]
    return !!value && !isValid(value)
  })
  const blockingTab = missingName
    ? "contacts.generalData"
    : invalidSepa
      ? "contacts.accounting"
      : null

  const { data: companyTags = [] } = useCompanyTags(organizationId)
  const createTagMutation = useCreateCompanyTag()
  const deleteTagMutation = useDeleteCompanyTag()
  const deleteCompanyMutation = useDeleteCompany()

  useEffect(() => {
    previousOpenRef.current = open
  }, [open])

  useEffect(() => {
    previousCompanyIdRef.current = companyId
  }, [companyId])

  useEffect(() => {
    if (!open || !companyId) {
      setIsWaitingFreshCompany(false)
      return
    }

    let isCancelled = false
    setIsWaitingFreshCompany(true)

    const refreshCompanyOnOpen = async () => {
      try {
        await refetchCompany()
      } catch (error) {
        console.error("Error refreshing company on open:", error)
      } finally {
        if (!isCancelled) {
          setIsWaitingFreshCompany(false)
        }
      }
    }

    refreshCompanyOnOpen()

    return () => {
      isCancelled = true
    }
  }, [open, companyId, refetchCompany])

  useEffect(() => {
    if (open) {
      if (companyId && hasRequestedCompanyData) {
        setFields({
          companyName: fetchedCompany.companyName || "",
          type: fetchedCompany.type || "company",
          id: fetchedCompany.id || "",
          category:
            fetchedCompany.category
              ?.map(simpleTag =>
                companyTags.find(
                  tag =>
                    tag.properties.name === simpleTag.tag_name &&
                    tag.properties.color === simpleTag.tag_color,
                ),
              )
              .filter(Boolean) || [],
          fiscalIdType: fetchedCompany.fiscalIdType || "",
          fiscalIdNumber: fetchedCompany.fiscalIdNumber || "",
          cp: fetchedCompany.cp || "",
          street1: fetchedCompany.street1 || "",
          street2: fetchedCompany.street2 || "",
          city: fetchedCompany.city || "",
          state: fetchedCompany.state || "",
          country: fetchedCompany.country || "",
          contability: contabilityFrom(fetchedCompany.contability),
          notes: fetchedCompany.notes || "",
        })
      } else if (!companyId) {
        setFields({
          companyName: "",
          type: "company",
          id: "",
          category: [],
          fiscalIdType: "",
          fiscalIdNumber: "",
          cp: "",
          street1: "",
          street2: "",
          city: "",
          state: "",
          country: "",
          contability: emptyContability(),
          notes: "",
        })
      }
      setTabValue(0)
      setErrors({})
      setIsSaving(false)
    }
  }, [open, companyId, fetchedCompany, companyTags, hasRequestedCompanyData])

  useEffect(() => {
    if (shouldOpen && onReady) {
      onReady()
    }
  }, [shouldOpen, onReady])

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleChange = e => {
    const { name, value } = e.target
    if (name.startsWith("contability.")) {
      const field = name.split(".")[1]
      setFields(prev => ({
        ...prev,
        contability: {
          ...prev.contability,
          [field]: value,
        },
      }))
    } else if (name === "type") {
      setFields(prev => ({
        ...prev,
        type: value,
        companyName:
          value === "individual" && contactFullName
            ? contactFullName
            : prev.companyName,
      }))
    } else {
      setFields(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleCategoryChange = selectedTags => {
    setFields(prev => ({ ...prev, category: selectedTags }))
  }

  const handleCreateTag = async (tagName, tagColor) => {
    try {
      await createTagMutation.mutateAsync({
        organizationId,
        tagName,
        tagColor,
      })
      queryClient.invalidateQueries({
        queryKey: ["companyTags", organizationId],
      })
    } catch (error) {
      console.error("Error creating tag:", error)
    }
  }

  const handleDeleteTag = async tag => {
    try {
      await deleteTagMutation.mutateAsync({
        organizationId,
        tagSKs: [tag.SK],
      })
      queryClient.invalidateQueries({
        queryKey: ["companyTags", organizationId],
      })
    } catch (error) {
      console.error("Error deleting tag:", error)
    }
  }

  const handleSave = () => {
    // Validación
    const newErrors = {}
    if (!fields.companyName.trim()) {
      newErrors.companyName = true
    }
    setErrors(newErrors)

    if (Object.keys(newErrors).length > 0) {
      // The name lives on the first tab, so land the user on it: the error banner
      // sits in the footer and says nothing about where to look.
      setTabValue(TAB_GENERAL)
      return
    }

    const companyData = { ...fields }
    if (companyData.contability?.paymentPeriod === undefined) {
      companyData.contability.paymentPeriod = 0
    }

    // Normalised and checked here, not just on the fields' blur: saving with Enter or
    // while an input still holds focus never fires it. The backend rejects malformed
    // SEPA data too, but bailing here keeps the inline error next to the field instead
    // of surfacing a generic save failure.
    if (companyData.contability) {
      const sepa = { ...companyData.contability }
      const invalid = []
      for (const { field, normalize, isValid } of SEPA_FIELDS) {
        if (!sepa[field]) continue
        sepa[field] = normalize(sepa[field])
        if (!isValid(sepa[field])) invalid.push(field)
      }
      if (invalid.length > 0) {
        setFields(prev => ({ ...prev, contability: sepa }))
        setTabValue(TAB_ACCOUNTING)
        return
      }
      companyData.contability = sepa
    }

    if (companyData.category && companyData.category.length > 0) {
      companyData.category = companyData.category.map(tag => ({
        tag_name: tag.properties?.name || tag.tag_name,
        tag_color: tag.properties?.color || tag.tag_color,
      }))
    }

    setIsSaving(true)
    onSave({ company: companyData })
  }

  const handleClose = () => {
    onClose()
    setIsSaving(false)
  }

  return (
    <Dialog open={shouldOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {company
          ? i18n.t("contacts.editCompany")
          : i18n.t("contacts.newCompany")}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 2, height: "380px" }}>
        {companyId &&
        (isCompanyLoading ||
          (isCompanyFetching && !hasRequestedCompanyData)) ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="company modal tabs"
            >
              <Tab label={i18n.t("contacts.generalData")} />
              <Tab label={i18n.t("contacts.accounting")} />
              <Tab label={i18n.t("contacts.notesDocuments")} />
            </Tabs>
            <TabPanel value={tabValue} index={0}>
              <GeneralTab
                fields={fields}
                handleChange={handleChange}
                handleCategoryChange={handleCategoryChange}
                categoryOptions={companyTags}
                onCreateTag={handleCreateTag}
                onDeleteTag={handleDeleteTag}
                errors={errors}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <ContabilityTab
                fields={fields}
                handleChange={handleChange}
                organizationId={organizationId}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <NotesTab fields={fields} handleChange={handleChange} />
            </TabPanel>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "space-between" }}>
        {blockingTab && (
          <Typography variant="body2" color="error" sx={{ flexGrow: 1 }}>
            {i18n.t("contacts.fixErrorsInTab", { tab: i18n.t(blockingTab) })}
          </Typography>
        )}
        {company && company.SK ? (
          <Button
            onClick={() => setDeleteDialogOpen(true)}
            color="error"
            variant="outlined"
          >
            {i18n.t("contacts.deleteCompany")}
          </Button>
        ) : (
          <Box></Box>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            {i18n.t("common.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            color="branded"
            variant="contained"
            disabled={isSaving || !!blockingTab}
            sx={{ width: 120, height: 36 }}
          >
            {isSaving ? <CircularProgress size={20} /> : i18n.t("common.save")}
          </Button>
        </Box>
      </DialogActions>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle> {i18n.t("contacts.deleteCompany")}</DialogTitle>
        <DialogContent>
          <Typography>{i18n.t("contacts.deleteCompanyMessage")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">
            {i18n.t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              deleteCompanyMutation.mutate(
                { organizationId, companyId: company.SK },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ["companies"] })
                    setDeleteDialogOpen(false)
                    onClose()
                  },
                  onError: error => {
                    console.error("Error al eliminar compañía:", error)
                  },
                },
              )
            }}
            color="error"
            variant="contained"
          >
            {i18n.t("contacts.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

CompanyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
  company: PropTypes.object,
  contactFullName: PropTypes.string,
  onReady: PropTypes.func,
}

export default CompanyModal
